import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import TenantAdminJobProgressDialog from '../../components/sistema/TenantAdminJobProgressDialog';
import TenantOwnerLoginFields, { validateOwnerLogin } from '../../components/sistema/TenantOwnerLoginFields';
import tenantAdminService from '../../services/tenantAdminService';
import { TenantAdmin, TenantCloneJob } from '../../types';
import { getApiErrorMessage } from '../../utils/apiError';

const CODE_PATTERN = /^[A-Za-z0-9_-]+$/;

interface CloneFormState {
  code: string;
  name: string;
  domain: string;
  plan: string;
  expiresAtLocal: string;
  sourceTenantId: string;
  isActive: boolean;
  ownerUsername: string;
  ownerPassword: string;
  ownerPasswordConfirm: string;
}

const defaultForm: CloneFormState = {
  code: '',
  name: '',
  domain: '',
  plan: '',
  expiresAtLocal: '',
  sourceTenantId: '',
  isActive: true,
  ownerUsername: '',
  ownerPassword: '',
  ownerPasswordConfirm: '',
};

const fromDatetimeLocalValue = (value: string): string | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
};

function validateCloneForm(form: CloneFormState): string | null {
  const code = form.code.trim();
  const name = form.name.trim();
  if (!form.sourceTenantId) return 'El tenant origen es obligatorio';
  if (!code) return 'El código es obligatorio';
  if (!name) return 'El nombre es obligatorio';
  if (code.length > 50) return 'El código no puede superar 50 caracteres';
  if (name.length > 200) return 'El nombre no puede superar 200 caracteres';
  if (/\s/.test(code)) return 'El código no puede contener espacios';
  if (!CODE_PATTERN.test(code)) {
    return 'El código solo puede contener letras, números, guiones y guiones bajos';
  }
  if (form.domain.trim().length > 255) return 'El dominio no puede superar 255 caracteres';
  if (form.plan.trim().length > 50) return 'El plan no puede superar 50 caracteres';
  if (form.expiresAtLocal.trim() && !fromDatetimeLocalValue(form.expiresAtLocal)) {
    return 'La fecha de vencimiento no es válida';
  }
  return validateOwnerLogin(form.ownerUsername, form.ownerPassword, form.ownerPasswordConfirm);
}

const TenantCloneForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [formData, setFormData] = useState<CloneFormState>({ ...defaultForm });
  const [sources, setSources] = useState<TenantAdmin[]>([]);
  const [loadingSources, setLoadingSources] = useState(false);
  const [saving, setSaving] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [cloneJob, setCloneJob] = useState<TenantCloneJob | null>(null);
  const createdTenantIdRef = useRef(0);

  useEffect(() => {
    const loadSources = async () => {
      try {
        setLoadingSources(true);
        const data = await tenantAdminService.listPaged({ pageNumber: 1, pageSize: 100 });
        setSources(data.items.filter((tenant) => !tenant.parentTenantId && tenant.isActive));
      } catch (error: unknown) {
        enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar tenants origen'), {
          variant: 'error',
        });
      } finally {
        setLoadingSources(false);
      }
    };
    void loadSources();
  }, [enqueueSnackbar]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const loadCloneJob = useCallback(async (id: string) => {
    const job = await tenantAdminService.getCloneJob(id);
    setCloneJob(job);
    if (job.tenantId > 0) createdTenantIdRef.current = job.tenantId;
    return job;
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validateCloneForm(formData);
    if (validationError) {
      enqueueSnackbar(validationError, { variant: 'warning' });
      return;
    }

    try {
      setSaving(true);
      const started = await tenantAdminService.clone({
        code: formData.code.trim(),
        name: formData.name.trim(),
        sourceTenantId: Number(formData.sourceTenantId),
        domain: formData.domain.trim() || undefined,
        plan: formData.plan.trim() || undefined,
        expiresAt: fromDatetimeLocalValue(formData.expiresAtLocal),
        isActive: formData.isActive,
        ownerUsername: formData.ownerUsername.trim(),
        ownerPassword: formData.ownerPassword,
      });
      setJobId(started.jobId);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo iniciar el clonado'), {
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCloneSucceeded = useCallback(() => {
    enqueueSnackbar(
      `Tenant clonado. ${formData.ownerUsername.trim()} quedó como owner de la base. Continuá con la limpieza de datos.`,
      { variant: 'success' },
    );
    const createdId = createdTenantIdRef.current;
    if (createdId) {
      navigate(`/sistema/tenants/${createdId}/limpiar`);
    }
  }, [enqueueSnackbar, formData.ownerUsername, navigate]);

  const handleJobClose = () => {
    const createdId = cloneJob?.tenantId;
    setJobId(null);
    if (cloneJob?.status === 'Succeeded' && createdId) {
      navigate(`/sistema/tenants/${createdId}/limpiar`);
      return;
    }
    setCloneJob(null);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
            sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/sistema/tenants')}
              sx={{ textTransform: 'none' }}
            >
              Administración de Tenants
            </Button>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Crear desde tenant
            </Typography>
          </Stack>

          {loadingSources ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={3} maxWidth={900}>
                <Grid item xs={12}>
                  <FormControl fullWidth required disabled={saving}>
                    <InputLabel id="source-tenant-label">Tenant origen</InputLabel>
                    <Select
                      labelId="source-tenant-label"
                      label="Tenant origen"
                      value={formData.sourceTenantId}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, sourceTenantId: String(e.target.value) }))
                      }
                    >
                      {sources.map((tenant) => (
                        <MenuItem key={tenant.id} value={String(tenant.id)}>
                          {tenant.code} — {tenant.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Se copia la base de datos de este tenant. No se incluyen hijas (maestros por vistas).
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="code"
                    label="Código"
                    value={formData.code}
                    onChange={handleChange}
                    required
                    fullWidth
                    disabled={saving}
                    helperText="Identificador único alfanumérico (sin espacios)"
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="name"
                    label="Nombre"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    fullWidth
                    disabled={saving}
                    helperText="Razón social o nombre comercial"
                    inputProps={{ maxLength: 200 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="domain"
                    label="Dominio"
                    value={formData.domain}
                    onChange={handleChange}
                    fullWidth
                    disabled={saving}
                    helperText="Dominio público del tenant (opcional, único)"
                    inputProps={{ maxLength: 255 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="plan"
                    label="Plan"
                    value={formData.plan}
                    onChange={handleChange}
                    fullWidth
                    disabled={saving}
                    helperText="Plan comercial o tipo de licencia (opcional)"
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="expiresAtLocal"
                    label="Vencimiento"
                    type="datetime-local"
                    value={formData.expiresAtLocal}
                    onChange={handleChange}
                    fullWidth
                    disabled={saving}
                    helperText="Fecha de vencimiento del tenant (opcional)"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <TenantOwnerLoginFields
                  username={formData.ownerUsername}
                  password={formData.ownerPassword}
                  confirmPassword={formData.ownerPasswordConfirm}
                  disabled={saving}
                  onChange={handleChange}
                />
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isActive}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, isActive: e.target.checked }))
                        }
                        disabled={saving}
                      />
                    }
                    label="Tenant activo"
                  />
                </Grid>
              </Grid>
              <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={saving}
                  onClick={() => navigate('/sistema/tenants')}
                >
                  Cancelar
                </Button>
                <Button type="submit" variant="contained" color="primary" disabled={saving || sources.length === 0}>
                  {saving ? 'Iniciando...' : 'Crear y clonar base'}
                </Button>
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>
      <TenantAdminJobProgressDialog
        open={Boolean(jobId)}
        title={`Clonar tenant ${formData.code || ''}`}
        description="Se genera un backup COPY_ONLY de la BD origen y se restaura como base del nuevo tenant. No cierre esta ventana hasta que termine."
        jobId={jobId}
        loadJob={loadCloneJob}
        onClose={handleJobClose}
        onSucceeded={handleCloneSucceeded}
      />
    </Box>
  );
};

export default TenantCloneForm;
